import React, { useState } from "react";
import PropTypes from "prop-types";
import { useErrorHandler } from "react-error-boundary";
import {
  destroyInvite,
  loadInvites,
  resendInvite,
} from "../../../queries/invite";
import { useMutation, useQuery } from "react-query";
import {
  removeItem,
  updateObjectInArray,
} from "../../../redux/reducers/helpers";
import {
  blockUser,
  destroyUser,
  loadUsers,
} from "../../../queries/consumer/users";
import InviteModal from "../invitations/InviteModal";
import EditOrganizationForm from "./EditOrganizationForm";
import {
  loadOrganization,
  updateOrganization,
} from "../../../queries/organization";
import { createErrors } from "../../../queries/helpers";
import OrganizationUsersInvitesTable from "./OrganizationUsersInvitesTable";
import isEmpty from "lodash/isEmpty";
import { addCountry } from "../../helpers";

const OrganizationPage = ({ currentUser, queryClient, toast }) => {
  const handleError = useErrorHandler();
  const [modal, setModal] = useState(false);

  const fetchInvite = async () =>
    await loadInvites()
      .then((data) => {
        return data.results;
      })
      .catch((error) => {
        handleError(error);
      });

  const {
    isLoading: isLoadingInvites,
    isError: isErrorInvites,
    data: invites,
  } = useQuery(
    "invites",
    () =>
      fetchInvite().then((data) => {
        return data;
      }),
    {
      keepPreviousData: true,
    }
  );

  const {
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
    data: users,
  } = useQuery("users", () => loadUsers());

  const {
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization,
    data: organization,
  } = useQuery(
    "organization",
    async () =>
      await loadOrganization(currentUser.organization.id)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          handleError(error);
        })
  );

  const editOrganization = useMutation(async (formData) => {
    return await updateOrganization(formData, currentUser.organization.id);
  });

  const onSubmit = async (values) => {
    let new_values = { ...values };
    new_values = addCountry(new_values);
    return await editOrganization
      .mutateAsync(new_values)
      .then((organization) => {
        queryClient.setQueryData("organization", organization);
        toast.success([
          {
            title: "Organization has been update",
            body: organization.legalName,
          },
        ]);
      })
      .catch((error) => {
        toast.error([
          {
            title: "Failed to update organization",
            body: `${error.response.data.errors
              .map((error) => error.detail)
              .join(", ")}`,
          },
        ]);
        return createErrors(
          error,
          "Failed to update organization",
          handleError
        );
      });
  };

  const deleteInvite = useMutation(async (invite) => {
    return destroyInvite(invite.id).then((data) => {
      queryClient.setQueryData("invites", removeItem(invites, data));
    });
  });

  const deleteUser = useMutation(async (user) => {
    return destroyUser(user.id).then((data) => {
      queryClient.setQueryData("users", removeItem(users, data));
    });
  });

  const userBlock = useMutation(async (user) => {
    return blockUser(user.blocked, user.id).then((data) => {
      queryClient.setQueryData("users", updateObjectInArray(users, data));
    });
  });

  const sendInvite = useMutation(async (invite) => {
    if (
      confirm(
        `Are you sure that you want to resend invitation for ${invite.email} ?`
      )
    ) {
      return await resendInvite(invite.id).then((data) => {
        queryClient.setQueryData("invites", updateObjectInArray(invites, data));
        toast({
          title: "Invitation has been send",
          body: invite.email,
        });
      });
    }
  });

  const toggle = () => setModal(!modal);
  return (
    <div className="container">
      <h3>Organization</h3>
      <p className="mb-5">
        Adjust your organization settings, invite new users to join or manage
        existing users.
      </p>

      <ul
        className="d-lg-none nav nav-pills justify-content-sm-center mb-5"
        role="tablist"
      >
        <li className="nav-item">
          <a
            className="nav-link active"
            data-toggle="tab"
            href="#settings"
            role="tab"
          >
            Settings
          </a>
        </li>
        <li className="nav-item">
          <a className="nav-link" data-toggle="tab" href="#users" role="tab">
            Users
          </a>
        </li>
      </ul>
      <div className="tab-content tabs-lg-visible d-flex flex-column flex-lg-row">
        {!isEmpty(organization) &&
          !isLoadingOrganization &&
          !isErrorOrganization && (
            <EditOrganizationForm
              toggle={toggle}
              onSubmit={onSubmit}
              organization={organization}
            />
          )}
        <div id="users" className="tab-pane col">
          <div className="d-flex align-items-center mb-4">
            <h4 className="m-0">Users</h4>
            <a
              className="btn btn-sm btn-ghost ml-auto"
              data-toggle="modal"
              data-target="#invitation-modal"
              onClick={toggle}
            >
              Invite users
            </a>
          </div>
          {!isEmpty(users) &&
            !isLoadingUsers &&
            !isLoadingInvites &&
            !isErrorUsers &&
            !isErrorInvites && (
              <OrganizationUsersInvitesTable
                users={users}
                invites={invites}
                currentUser={currentUser}
                sendInvite={sendInvite}
                deleteInvite={deleteInvite}
                deleteUser={deleteUser}
                userBlock={userBlock}
              />
            )}
        </div>
      </div>
      <InviteModal
        queryDataString={"invites"}
        modal={modal}
        toggle={toggle}
        handleError={handleError}
        queryClient={queryClient}
      />
    </div>
  );
};

OrganizationPage.propTypes = {
  currentUser: PropTypes.object.isRequired,
  queryClient: PropTypes.object.isRequired,
};

export default OrganizationPage;
